import { isDateToday, secondsToMinutesRounded, WindowInfo, ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import { VisitHbsKitConfigMapper } from '@csp/csp-common-hbs';
import { BioSample, KitTypeAndDescriptions } from '@csp/csp-common-hbs-model';
import {
  BookedVisitStatusType,
  Maybe,
  Patient,
  PatientFinalContactStatusType,
  StudyVisitConfig,
  VisitStatusType,
  VisitVariantType,
  ZonedDateTime,
} from '@csp/csp-common-model';
import {
  RequestWindow,
  RolloutRequestResult,
  ScheduleReferenceDates,
  ScheduleVersionRequestRef,
  SchedulingService,
} from '@csp/csp-common-scheduling';
import { isDefined } from '@csp/csp-common-util';
import {
  BookedVisit,
  ConfirmationStateType,
  PatientVisitTextsMapper,
  ScheduleVisitsContent,
  VisitConfirmationStatusType,
  VisitRequest,
} from '@csp/csp-common-visits';
import { WebHcpPatientVisitEntry, WebHcpStaticTimeUnitsBlock } from '@csp/csp-web-content-definitions';
import { AutocompleteOption, ChipStatusProps } from '@csp/csp-web-ui';
import { orderBy, partition, template } from 'lodash';
import { BookedPatientVisit } from '../model/BookedPatientVisit';
import { BookedPatientVisitTexts } from '../model/BookedPatientVisitTexts';
import { BookedVisitComparison } from '../model/BookedVisitComparison';

const FOURTY_EIGHT_HOURS_MILLIS = 48 * 3600 * 1000;

const toDisplayVideoLinkInfo = (
  joinVideoInAdvanceSecs: number,
  units: WebHcpStaticTimeUnitsBlock,
  patientVisitTexts: WebHcpPatientVisitEntry,
): string =>
  template(patientVisitTexts.visitDetails.body.displayVideoLinkInfo.text)({
    value: joinVideoInAdvanceSecs && secondsToMinutesRounded(joinVideoInAdvanceSecs),
    timeUnit: units?.enum.MINUTES,
  });

const toBookedVisitStatus = (
  patient: Patient,
  isBooked: boolean,
  startTimestamp: Maybe<ZonedDateTime>,
  visitVariant: VisitVariantType,
): BookedVisitStatusType => {
  const isPassed = isBooked && BookedVisit.isVisitStartPassed(startTimestamp);
  const isOverdue =
    isBooked && startTimestamp && startTimestamp.unixTimeMillis < Date.now() - FOURTY_EIGHT_HOURS_MILLIS;

  if (
    visitVariant === VisitVariantType.CLOSE_OUT &&
    patient.statuses.finalContactStatus.status === PatientFinalContactStatusType.CONTACT_COMPLETED
  ) {
    return BookedVisitStatusType.CLOSE_OUT_COMPLETED;
  } else if (visitVariant === VisitVariantType.CLOSE_OUT && isOverdue) {
    return BookedVisitStatusType.CLOSE_OUT_OVERDUE;
  } else if (isPassed) {
    return BookedVisitStatusType.PASSED;
  } else if (isBooked) {
    return BookedVisitStatusType.BOOKED;
  } else {
    return BookedVisitStatusType.UNKNOWN;
  }
};

const toBookedPatientVisitTexts = (
  visit: BookedVisit,
  patient: Patient,
  bookedStatus: BookedVisitStatusType,
  { joinVideoInAdvanceSecs }: StudyVisitConfig,
  patientVisitTexts: WebHcpPatientVisitEntry,
  visitsContent: ScheduleVisitsContent,
  unitTimeTexts: WebHcpStaticTimeUnitsBlock,
  visitRequest: VisitRequest,
): BookedPatientVisitTexts => {
  const locationInfo = patient.site?.locations?.find(location => location.locationId === visit.location.locationRef);
  const siteAndLocationStr = [locationInfo?.name, patient.site?.name].filter(isDefined).join(' · ');
  const newPatientVisitTexts = PatientVisitTextsMapper.fromBookedVisit(
    visit,
    bookedStatus,
    patientVisitTexts,
    visitsContent,
    visitRequest,
  );

  return {
    ...newPatientVisitTexts,
    siteAndLocation: {
      label: patientVisitTexts.visitDetails.textLabel.location.label,
      value: siteAndLocationStr,
    },
    dateAndTime: {
      label: patientVisitTexts.visitDetails.textLabel.dateAndTime.label,
      value: `${visit.startDateStr} ${visit.startTimeStr}`,
    },
    location: {
      label: patientVisitTexts.visitDetails.textLabel.location.label,
      value: locationInfo?.name ? locationInfo?.name : patient.site?.name,
    },
    window: visit.window.hasWindowStartAndEnd
      ? {
          label: patientVisitTexts.visitDetails.textLabel.bookingWindow.label,
          value: visit.window.windowDateStr,
        }
      : undefined,
    displayVideoLinkInfo: toDisplayVideoLinkInfo(joinVideoInAdvanceSecs, unitTimeTexts, patientVisitTexts),
  };
};

const toConfirmationState = (
  visit: BookedVisit,
  patient: Patient,
  visitRequests: VisitRequest,
): ConfirmationStateType => {
  const isConfirmNeeded =
    (visit.confirmationStatus.value === VisitConfirmationStatusType.NOT_CONFIRMED ||
      visit.confirmationStatus.value === VisitConfirmationStatusType.UNKNOWN) &&
    visit.isVisitStarted &&
    !!visitRequests?.requireConfirmation;

  if (isConfirmNeeded && patient.canConfirmVisit) {
    return ConfirmationStateType.CONFIRM_NEEDED;
  } else if (
    visit.confirmationStatus.value === VisitConfirmationStatusType.CONFIRMED &&
    visitRequests?.requireConfirmation
  ) {
    return ConfirmationStateType.CONFIRMED;
  } else if (isConfirmNeeded && patient.isReadonly) {
    return ConfirmationStateType.CONFIRMATION_MISSED;
  } else {
    return ConfirmationStateType.NO_CONFIRM_NEEDED;
  }
};

const toChipStatus = (
  confirmationStateType: ConfirmationStateType,
  patientVisitTexts: WebHcpPatientVisitEntry,
): Maybe<ChipStatusProps> => {
  switch (confirmationStateType) {
    case ConfirmationStateType.CONFIRM_NEEDED:
      return {
        label: patientVisitTexts.patientVisits.textLabel.toConfirm.label,
        variant: 'contained',
        color: 'warning',
      };
    case ConfirmationStateType.CONFIRMED:
      return {
        label: patientVisitTexts.patientVisits.textLabel.confirmed.label,
        variant: 'outlined',
        color: 'inactive',
      };
    case ConfirmationStateType.CONFIRMATION_MISSED:
      return {
        label: patientVisitTexts.patientVisits.textLabel.noConfirmNeeded.label,
        variant: 'outlined',
        color: 'inactive',
      };
    default:
      return undefined;
  }
};

const isSameWindow = (window1?: RequestWindow, window2?: WindowInfo | RequestWindow): boolean => {
  if (!window1 || !window2) {
    return false;
  } else {
    return (
      window1?.windowStart?.unixTimeMillis === window2?.windowStart?.unixTimeMillis &&
      window1?.windowEnd?.unixTimeMillis === window2?.windowEnd?.unixTimeMillis
    );
  }
};

const from = ({
  visit,
  visitRequests,
  patient,
  studyVisitConfig,
  patientVisitTexts,
  unitTimeTexts,
  visitsContent,
  bioSamples,
  allKitsWithDesc,
  rolloutRequestResult,
}: {
  visit: BookedVisit;
  visitRequests: VisitRequest[];
  patient: Patient;
  studyVisitConfig: StudyVisitConfig;
  patientVisitTexts: WebHcpPatientVisitEntry;
  unitTimeTexts: WebHcpStaticTimeUnitsBlock;
  visitsContent: ScheduleVisitsContent;
  bioSamples: BioSample[];
  allKitsWithDesc: Maybe<KitTypeAndDescriptions>;
  rolloutRequestResult: RolloutRequestResult;
}): BookedPatientVisit => {
  const visitRequest =
    visitRequests.find(req => ScheduleVersionRequestRef.isSameRequestRef(req, visit.requestRef)) ??
    VisitRequest.toUnknownVisitRequest(visit.requestRef);
  const locationInfo = patient.site?.locations?.find(location => location.locationId === visit.location.locationRef);

  const bookedStatus = toBookedVisitStatus(
    patient,
    visit.status === VisitStatusType.BOOKED,
    visit.startTimestamp,
    visitRequest.variant,
  );

  const texts = toBookedPatientVisitTexts(
    visit,
    patient,
    bookedStatus,
    studyVisitConfig,
    patientVisitTexts,
    visitsContent,
    unitTimeTexts,
    visitRequest,
  );

  // See if booking window has changed, e.g by a data change.
  const newWindow = rolloutRequestResult.requestWindows[0];
  const hasChanged = !isSameWindow(newWindow, visit.window);
  let visitWindow = visit.window;

  // Override booking window info with new window if it has changed.
  if (hasChanged && newWindow) {
    visitWindow = WindowInfo.from(newWindow.windowStart, newWindow.windowEnd);
  }

  const isBookedWithinWindow = visit.startTimestamp
    ? !visit.window.hasWindow || visit.window.isWithinWindow(visit.startTimestamp)
    : false;

  const compare = async (
    oldReferenceDates: ScheduleReferenceDates,
    newReferenceDates: ScheduleReferenceDates,
  ): Promise<BookedVisitComparison> => {
    const rolloutOld = await SchedulingService.rolloutRequest({
      request: visitRequest,
      scheduleReferenceDates: oldReferenceDates,
      fromRepeatIndex: visit.requestRef.repeatIndex,
      limitRepeat: 1,
    });

    const rolloutNew = await SchedulingService.rolloutRequest({
      request: visitRequest,
      scheduleReferenceDates: newReferenceDates,
      fromRepeatIndex: visit.requestRef.repeatIndex,
      limitRepeat: 1,
    });

    const oldRequestWindow = rolloutOld.requestWindows[0];
    const newRequestWindow = rolloutNew.requestWindows[0];

    const hasChanged = !isSameWindow(newRequestWindow, oldRequestWindow);

    const newWindowInfo = WindowInfo.from(newRequestWindow?.windowStart, newRequestWindow?.windowEnd);
    const bookedDate = ZonedDateTimeFactory.fromDateString(visit.startDateStr);
    const isBookedDateWithinNewWindow = newRequestWindow ? newWindowInfo.isWithinWindow(bookedDate) : false;

    return { hasChanged, isBookedDateWithinNewWindow, newWindow: newRequestWindow };
  };

  const confirmationState = toConfirmationState(visit, patient, visitRequest);

  const hbsKitConfig = VisitHbsKitConfigMapper.from(
    visitRequest.hbs,
    allKitsWithDesc?.items || [],
    texts.bioSampleKitDescriptions?.map(text => text.sample_kit?.description).filter(isDefined),
  );

  const hbsAnalyteTypes =
    texts.bioSampleAnalyteTypes?.map(analyteType => analyteType.analyte_name).filter(isDefined) ?? [];

  const hbsSampleTypes = texts.bioSampleSampleTypes?.map(sampleType => sampleType.sample_name).filter(isDefined) ?? [];

  const hbsProcessedSampleTypes =
    texts.bioSampleProcessedSampleTypes?.map(sampleType => sampleType.processed_sample_name).filter(isDefined) ?? [];

  return {
    ...visit,
    window: visitWindow,
    patient,
    locationInfo,
    confirmationState,
    chipStatus: toChipStatus(confirmationState, patientVisitTexts),
    texts,
    visitRequest,
    isVariant: (variant: VisitVariantType): boolean => visitRequest.variant === variant,
    isVisitConfirmed: confirmationState === ConfirmationStateType.CONFIRMED,
    isConfirmNeeded: confirmationState === ConfirmationStateType.CONFIRM_NEEDED,
    bookedStatus,
    isBookedWithinWindow,
    bioSamples,
    hbsKitConfig,
    hbsAnalyteTypes,
    hbsSampleTypes,
    hbsProcessedSampleTypes,
    compare,
  };
};

const toAutocompleteOptions = (visits: BookedPatientVisit[] = []): AutocompleteOption[] =>
  visits.map(
    ({
      visitId,
      texts: {
        visitName: { value: visitName },
        status: { value: status },
      },
      startDateTimeStr,
    }) => ({
      value: visitId,
      title: `${visitName} - ${startDateTimeStr} - ${status}`,
    }),
  );

const toAutocompleteOptionsDescending = (visits: BookedPatientVisit[] = []): AutocompleteOption[] => {
  const visitsDescending = [...visits].sort(
    (visit1, visit2) => (visit2.startTimestamp?.unixTimeMillis || 0) - (visit1.startTimestamp?.unixTimeMillis || 0),
  );
  return toAutocompleteOptions(visitsDescending);
};

const toAutocompleteOptionsRecentFirst = (visits: BookedPatientVisit[] = []): AutocompleteOption[] => {
  const visitsDescending = orderBy(visits, visit => visit.startTimestamp?.unixTimeMillis ?? 0, 'desc');
  const visitsStartedFirst = partition(
    visitsDescending,
    visit => isDateToday(visit.startTimestamp) || visit.isVisitStarted,
  );
  return toAutocompleteOptions(visitsStartedFirst.flat());
};

export const BookedPatientVisitMapper = {
  from,
  toBookedVisitStatus,
  toAutocompleteOptionsDescending,
  toAutocompleteOptionsRecentFirst,
};
